import { Editor, Element as SlateElement, Transforms, Range } from 'slate';
import type { CustomEditor, CustomElement, BlockType, MarkType } from './types';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

export const isMarkActive = (editor: CustomEditor, format: MarkType): boolean => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor: CustomEditor, format: MarkType): void => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const isBlockActive = (editor: CustomEditor, format: BlockType): boolean => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    })
  );

  return !!match;
};

export const toggleBlock = (editor: CustomEditor, format: BlockType): void => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type),
    split: true,
  });

  const newProperties: Partial<CustomElement> = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  };
  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!isActive && isList) {
    const block: CustomElement = { type: format as 'bulleted-list' | 'numbered-list', children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const isLinkActive = (editor: CustomEditor): boolean => {
  const [link] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!link;
};

export const insertLink = (editor: CustomEditor, url: string): void => {
  if (editor.selection) {
    wrapLink(editor, url);
  }
};

export const wrapLink = (editor: CustomEditor, url: string): void => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  const link: CustomElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const unwrapLink = (editor: CustomEditor): void => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

export const serializeToPlainText = (nodes: CustomElement[]): string => {
  return nodes
    .map((n) => {
      if ('text' in n) {
        return n.text;
      }
      if ('children' in n) {
        return serializeToPlainText(n.children as CustomElement[]);
      }
      return '';
    })
    .join('\n');
};

export const createEmptyValue = (): CustomElement[] => [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export const parseSlateValue = (content: string): CustomElement[] => {
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    if (content && content.trim()) {
      return [
        {
          type: 'paragraph',
          children: [{ text: content }],
        },
      ];
    }
  }
  return createEmptyValue();
};

export const stringifySlateValue = (value: CustomElement[]): string => {
  return JSON.stringify(value);
};

export const isContentEmpty = (value: CustomElement[]): boolean => {
  if (!value || value.length === 0) return true;

  const text = serializeToPlainText(value);
  return text.trim().length === 0;
};

export const getCharacterCount = (value: CustomElement[]): number => {
  const text = serializeToPlainText(value);
  return text.length;
};
